import { defineStore } from 'pinia';
import { ref } from 'vue';
import { DefinitionType } from '@/enums/DefinitionType.ts';
import { definitionApi } from '@/api/powerMarkets/definitionApi.ts';
import { DataApi } from '@/api/storageTracking/DataApi.ts';
import { deltaGptApi } from '@/api/deltaGpt/deltaGptApi.ts';
import { IDefinitionsHolder } from '@/interfaces/storageTracking/IDefinitionsHolder.ts';
import { IDefinition, IDefinitionSearchInfo, ISparkDefinitionSearchInfo } from '@/interfaces/powerMarkets/static/IDefinitionSearchInfo.ts';

function stripHtmlTags (value?: string | null): string {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function buildSparkDefinitionSearchQuery (definition: {
    documentTitle: string;
    documentSubtitle?: string;
    description?: string;
    bodyHtml?: string;
}): string {
    return [
        definition.documentTitle,
        definition.documentSubtitle,
        definition.description,
        stripHtmlTags(definition.bodyHtml),
    ].filter(Boolean).join(' ').toLowerCase();
}

function convertSparkDefinitionSearchInfo (): (definition: ISparkDefinitionSearchInfo) => IDefinitionSearchInfo {
    return definition => ({
        colour: 'var(--accentColour)',
        icon: 'book',
        id: definition.id,
        searchQuery: definition.searchQuery.toLowerCase(),
        title: definition.title,
        source: definition.source,
        description: definition.description,
        documentShortCode: definition.documentShortCode,
        publicationDate: definition.publicationDate,
        updatedAt: definition.updatedAt ?? undefined,
        hasAccess: definition.hasAccess,
    });
}

function convertSparkDefinition (definitionId: string, sparkContentDefinition: Awaited<ReturnType<typeof deltaGptApi.getSparkContentDetails>>): IDefinition {
    return {
        colour: 'var(--accentColour)',
        icon: 'book',
        id: definitionId,
        searchQuery: buildSparkDefinitionSearchQuery(sparkContentDefinition),
        title: sparkContentDefinition.documentTitle,
        source: 'spark',
        bodyHtml: sparkContentDefinition.bodyHtml,
        description: sparkContentDefinition.description,
        documentShortCode: sparkContentDefinition.documentShortCode,
        publicationDate: sparkContentDefinition.publicationDate,
        updatedAt: sparkContentDefinition.updatedAt ?? undefined,
        hasAccess: sparkContentDefinition.hasAccess,
        dataSources: null,
        dataSourcesByCountry: {},
        hasHistorics: false,
        hasHistoryOfForecast: false,
        hideFromFrontEnd: true,
        isEuropean: false,
        isPlant: false,
        options: {},
        otherTitles: [],
        relatedDynamicCharts: [],
        relatedDynamicSeries: [],
        relatedPlantCharts: [],
        relatedPlantSeries: [],
        relatedPresetDashboards: null,
        sections: [],
        sourceUrl: null,
        type: DefinitionType.Glossary,
        units: [],
    };
}

function findDefinitionSearchInfo (definitions: Array<IDefinitionSearchInfo>, id: string): IDefinitionSearchInfo | undefined {
    return definitions.find(definition => definition.id === id);
}

export const useDefinitionStore = defineStore('definitionsStore', () => {
    const searchInfo = ref<Array<IDefinitionSearchInfo>>([]);
    const definitionsById = ref<Record<string, IDefinition>>({});
    const storageTrackingDefinitions = ref<IDefinitionsHolder>();

    async function fetchAllDefinitionSearchInfo (): Promise<void> {
        const [allDefinitions, sparkDefinitions] = await Promise.all([
            definitionApi.getAllDefinitionSearchInfo(),
            deltaGptApi.getSparkDefinitionSearchInfo().catch(() => []),
        ]);

        if (allDefinitions.status !== 200) throw new Error(allDefinitions.statusText);

        searchInfo.value = [...allDefinitions.data, ...sparkDefinitions.map(convertSparkDefinitionSearchInfo())];
    }

    async function getDefinitionById (id: string): Promise<IDefinition | undefined> {
        if (definitionsById.value[id]) return definitionsById.value[id];

        let definitionSearchInfo = findDefinitionSearchInfo(searchInfo.value, id);

        if (!definitionSearchInfo && searchInfo.value.length === 0) {
            await fetchAllDefinitionSearchInfo().catch(() => undefined);
            definitionSearchInfo = findDefinitionSearchInfo(searchInfo.value, id);
        }

        if (definitionSearchInfo?.source === 'spark' && definitionSearchInfo.documentShortCode) {
            const sparkDefinition = await deltaGptApi.getSparkContentDetails(definitionSearchInfo.documentShortCode).catch(() => undefined);
            if (!sparkDefinition) return undefined;
            const definition = convertSparkDefinition(definitionSearchInfo.id, sparkDefinition);
            definitionsById.value[id] = definition;
            return definition;
        }

        const definition = await definitionApi.getDefinitionById(id);
        if (definition.status !== 200) return undefined;

        definitionsById.value[id] = definition.data;
        return definition.data;
    }

    async function getStorageTrackingDefinitions (): Promise<void> {
        storageTrackingDefinitions.value = await DataApi.getDefinitions();
    }

    return {
        storageTrackingDefinitions,
        definitionsById,
        searchInfo,
        fetchAllDefinitionSearchInfo,
        getDefinitionById,
        getStorageTrackingDefinitions,
    };
});
